import { useEffect } from 'react';
import { Body } from './partials/Body';
import { Footer } from './partials/Footer';
import { Header } from './partials/Header';
import { Provider } from './Provider';
import { useSetWindowContext } from './useWindowContext';

/**
 * Collection of components to allow for context based window layout.
 *
 * @example
 * <div onClick={() => setOpen(true)}>Click me!</div>
 * <WindowContext isOpen={open} setOpen={setOpen}>
 *     <WindowContext.Header>
 *         {* your header component here *}
 *     </WindowContext.Header>
 *     <WindowContext.Body>
 *         {* your body component here *}
 *     </WindowContext.Body>
 *     <WindowContext.Footer>
 *         {* your body component here *}
 *     </WindowContext.Footer>
 * </WindowContext>
 *
 */
const _WindowContext = ({
    isOpen, close, children: [Header, Body, Footer], deps = []
}: Props) => {

    const setWindowContext = useSetWindowContext();

    useEffect(() => {

        if (isOpen) {
            setWindowContext({ Header, Body, Footer });
        }

        return () => close();
    }, [isOpen, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
};

type Props = {
    isOpen: boolean;
    close: () => void;
    children: [
        ReturnType<typeof Header>,
        ReturnType<typeof Body>,
        ReturnType<typeof Footer>
    ];
    deps?: any[];
};

export const WindowContext = _WindowContext as typeof _WindowContext & {
    Header: typeof Header;
    Body: typeof Body;
    Footer: typeof Footer;
    Provider: typeof Provider;
};

WindowContext.Header = Header;
WindowContext.Body = Body;
WindowContext.Footer = Footer;
WindowContext.Provider = Provider;